import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import "../../scss/backoffice.scss";
import "../../scss/backend/super-admin-config.scss";
import { Sidemenu } from "./Sidemenu";
import { Topbar } from "./Topbar";
import { Footer } from "./Footers";
import { ToastContainer, toast } from "react-toastify";
import { BiPlusCircle } from "react-icons/bi";
import { RiDraggable } from "react-icons/ri";
import { LuSettings } from "react-icons/lu";
import { FaTrash } from "react-icons/fa";
import Switch from "react-switch";
import {
  GetMenuItems,
  updateOrder,
  updateStatus,
  updateMenu,
  deleteMenu,
} from "../../../src/services/ApiMenuService";
import CreateMenuServiceModal from "../../../src/modal/backend/CreateMenuServiceModal";
import EditMenuServiceModal from "../../../src/modal/backend/EditMenuServiceModal";

interface MenuItemProps {
  id: number;
  IndexNo: number;
  menuSites: menuSites[];
  serviceNameEn: string;
  serviceNameTh: string;
  serviceNameVn: string;
  iconImageBase64: string;
  bgColor: string;
  linkUrl: string;
  eventHandler: string;
  isActive: boolean;
}

interface menuSites {
  menuId: number;
  site: string;
  active: boolean;
}

const MenuItem: React.FC<{
  item: MenuItemProps;
  index: number;
  onSwitchChange: (index: number) => void;
  onEditClick: (item: MenuItemProps) => void;
  onDeleteClick: (id: number) => void;
}> = React.memo(
  ({ item, index, onSwitchChange, onEditClick, onDeleteClick }) => (
    <Draggable key={item.id} draggableId={`draggable-${item.id}`} index={index}>
      {(provided) => (
        <div
          className="menu-item"
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <div className="menu-icon">
            <div
              className="overlay-circle"
              style={{ background: item.bgColor }}
            >
              <img
                src={"data:image/png;base64," + item.iconImageBase64}
                height={50}
                alt={item.serviceNameEn}
              />
            </div>
          </div>
          <div className="title-box">{item.serviceNameEn}</div>
          {/* <div className="site">
            {item.menuSites.map((menuSite) => (
              <span
                key={menuSite.menuId}
                className={`site-list ${menuSite.active ? "active" : ""}`}
              >
                {menuSite.site}
              </span>
            ))}
          </div> */}
          <div className="menu-switch">
            <Switch
              onChange={() => onSwitchChange(index)}
              checked={item.isActive}
              aria-label={`${item.serviceNameEn} switch`}
            />
          </div>
          <div className="draggable-icon">
            <RiDraggable size={20} />
          </div>
          <div className="action-icons">
            <LuSettings
              size={20}
              onClick={() => onEditClick(item)}
              style={{ cursor: "pointer" }}
            />{" "}
            &nbsp;
            <FaTrash
              size={15}
              onClick={() => onDeleteClick(item.id)}
              style={{ cursor: "pointer" }}
            />{" "}
            &nbsp;
          </div>
        </div>
      )}
    </Draggable>
  )
);

export const SuperAdmin: React.FC = () => {
  const [hasError, setHasError] = useState(false);
  const [displayFullScreenLoading, setDisplayFullScreenLoading] =
    useState(false);
  const [menuItems, setMenuItems] = useState<MenuItemProps[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItemProps | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    setDisplayFullScreenLoading(true);
    GetMenuItems()
      .then((response) => {
        setMenuItems(response);
        setDisplayFullScreenLoading(false);
      })
      .catch((error) => {
        setHasError(true);
        toast.error("Failed to load menu items.");
        setDisplayFullScreenLoading(false);
      });
  }, []);

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(menuItems);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    const confirmSave = window.confirm("Do you want to save the new order?");
    if (confirmSave) {
      setMenuItems(reorderedItems);
      try {
        await updateOrder(reorderedItems);
        toast.success("Your changes have been saved.");
      } catch (error) {
        toast.error("Failed to update menu order.");
      }
    } else {
      toast.error("Your changes were not saved.");
    }
  };

  const handleSwitchChange = async (index: number) => {
    const newMenuItems = [...menuItems];
    newMenuItems[index].isActive = !newMenuItems[index].isActive;

    const confirmToggle = window.confirm(`Do you want to turn ${newMenuItems[index].isActive ? "on" : "off"} this menu?`);
    if (confirmToggle) {
      setMenuItems(newMenuItems);
      try {
        await updateStatus(newMenuItems[index].id, newMenuItems[index]);
        toast.success("Your changes have been saved.");
      } catch (error) {
        toast.error("Failed to update menu status.");
      }
    } else {
      toast.error("Your changes were not saved.");
    }
  };

  const handleCreateMenu = (menuData: {
    id: number;
    IndexNo: number;
    serviceNameEn: string;
    serviceNameTh: string;
    serviceNameVn: string;
    iconImageBase64: string;
    bgColor: string;
    linkUrl: string;
    menuSites: { menuId: number; site: string; active: boolean }[];
    eventHandler: string;
    isActive: boolean;
  }) => {
    // Add the new menu item to the end of the list
    const newMenuItem: MenuItemProps = {
      id: menuItems.length + 1, // This should be replaced with a unique ID from the backend
      IndexNo: menuItems.length, // Set the IndexNo to the current length of menuItems
      menuSites: menuData.menuSites,
      serviceNameEn: menuData.serviceNameEn,
      serviceNameTh: menuData.serviceNameTh,
      serviceNameVn: menuData.serviceNameVn,
      iconImageBase64: menuData.iconImageBase64,
      bgColor: menuData.bgColor,
      linkUrl: menuData.linkUrl,
      eventHandler: menuData.eventHandler,
      isActive: menuData.isActive,
    };
    setMenuItems([...menuItems, newMenuItem]);
  };

  const handleEditMenu = async (updatedMenuData: MenuItemProps) => {
    try {
      await updateMenu(updatedMenuData.id, updatedMenuData);
      setMenuItems((prevItems) =>
        prevItems.map((item) =>
          item.id === updatedMenuData.id ? updatedMenuData : item
        )
      );
      toast.success("Your changes have been saved.");
    } catch (error) {
      toast.error("Failed to update menu.");
    }
  };

  const handleDeleteMenu = async (id: number) => {
    const confirmDelete = window.confirm("Do you want to delete this menu?");
    if (confirmDelete) {
      try {
        await deleteMenu(id);
        setMenuItems((prevItems) => prevItems.filter((item) => item.id !== id));
        toast.success("The menu has been deleted.");
      } catch (error) {
        toast.error("Failed to delete menu.");
      }
    } else {
      toast.error("The menu was not deleted.");
    }
  };

  const openEditModal = useCallback((item: MenuItemProps) => {
    setEditingItem(item);
    setIsEditModalOpen(true);
  }, []);

  const toggleMobileView = () => {
    setIsMobileView(!isMobileView);
  };

  const memoizedMenuItems = useMemo(
    () =>
      menuItems.map((item, index) => (
        <MenuItem
          key={item.id}
          item={item}
          index={index}
          onSwitchChange={handleSwitchChange}
          onEditClick={openEditModal}
          onDeleteClick={handleDeleteMenu}
        />
      )),
    [menuItems, handleSwitchChange, openEditModal, handleDeleteMenu]
  );

  return (
    <>
      <div
        className={`container-fluid master-layout super-admin-config ${
          isMobileView ? "mobile-view-config" : ""
        }`}
      >
        <Sidemenu
          hasError={hasError}
          displayFullScreenLoading={displayFullScreenLoading}
          subTitle="Setting"
        />
        <div
          className={`main-container ${
            isMobileView ? "mobile-view-config" : ""
          }`}
        >
          <div className="col-12 mb-4">
            <Topbar />
          </div>
          <div className="container-fluid">
            <div className="title">Service Menu Management</div>
            <div className="menu-service-box">
              <div className="create-menu-service">
                <button
                  className="create-menu"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <BiPlusCircle size={20} /> Create Menu
                </button>
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="menuItems" direction="horizontal">
                  {(provided) => (
                    <div
                      className="menu-grid"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {memoizedMenuItems}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
          <div className="col-12">
            <Footer />
          </div>
        </div>
      </div>
      <ToastContainer />
      <CreateMenuServiceModal
        isOpen={isCreateModalOpen}
        onRequestClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateMenu}
        currentIndex={menuItems.length}
      />
      {editingItem && (
        <EditMenuServiceModal
          isOpen={isEditModalOpen}
          onRequestClose={() => setIsEditModalOpen(false)}
          menuItem={editingItem}
          onSubmit={handleEditMenu}
        />
      )}
    </>
  );
};
